<!--
  Ficha de uma unidade da rede (chamado NS 2026-0082, entrega 3a; Full): empresa, endereço e
  contato, mídias, contrato vigente, acompanhamento e os sócios (nome, CPF, nascimento, e-mail).
  Vale para a franquia do sistema e para a unidade fora do sistema. Na franquia, o que o cadastro
  já tem manda e aparece embaixo do campo. Gravar marca a ficha como editada: a importação da
  planilha passa a só preencher o que estiver vazio.
  Abre por eventBus.emit('modal.rede-ficha.open', { unidade: 'f:12' | 'e:5' }); avisa
  'rede.situacao.atualizada' (a lista recarrega o contrato).
-->
<template>
  <Modal v-if="isVisible" :title="ficha?.nome ?? t('Ficha da unidade')" :visible="isVisible" :closable="true" :style="{ width: '980px', maxWidth: '96vw' }" data-testid="rede-ficha-modal" @update:visible="fechar">
    <p v-if="!ficha" class="quieto">{{ t('Carregando…') }}</p>
    <form v-else class="ficha" data-testid="rede-ficha-form" @submit.prevent="gravar">
      <p class="quieto">
        <template v-if="!ficha.no_sistema">{{ t('Unidade fora do sistema (da planilha).') }} </template>
        <template v-if="ficha.numero_planilha">{{ t('Linha Nº') }} {{ ficha.numero_planilha }} {{ t('da planilha.') }} </template>
        <template v-if="ficha.origem === 'manual'">{{ t('Editada à mão: a importação só preenche o que estiver vazio.') }}</template>
      </p>
      <p v-if="ficha.alerta_contrato" class="alerta" :data-testid="`rede-ficha-alerta-${ficha.alerta_contrato}`">
        {{ ficha.alerta_contrato === 'vencido' ? t('Contrato vencido em') : t('Contrato vence em') }} {{ dataBr(ficha.vigencia_ate) }}
      </p>

      <section v-for="secao in SECOES" :key="secao.titulo" class="secao">
        <h4>{{ t(secao.titulo) }}</h4>
        <div class="grade">
          <div v-for="c in secao.campos" :key="c.campo" :class="['campo', { largo: c.tipo === 'texto' }]">
            <input-text-area v-if="c.tipo === 'texto'" v-model="valores[c.campo]" :elem-id="`rede-ficha-${c.campo}`" :data-testid="`rede-ficha-${c.campo}`" :label="t(c.rotulo)" :rows="2" />
            <input-date v-else-if="c.tipo === 'data'" v-model="valores[c.campo]" :elem-id="`rede-ficha-${c.campo}`" :data-testid="`rede-ficha-${c.campo}`" :label="t(c.rotulo)" />
            <input-text v-else v-model="valores[c.campo]" :elem-id="`rede-ficha-${c.campo}`" :data-testid="`rede-ficha-${c.campo}`" :label="t(c.rotulo)" />
            <small v-if="doCadastro[c.campo]" class="do-cadastro" :data-testid="`rede-ficha-cadastro-${c.campo}`">{{ t('No cadastro') }}: {{ doCadastro[c.campo] }}</small>
          </div>
        </div>
      </section>

      <section class="secao">
        <h4>{{ t('Sócios') }} <span class="quieto">({{ t('dado pessoal: só a franqueadora vê') }})</span></h4>
        <p v-if="socios.length === 0" class="quieto">{{ t('Nenhum sócio.') }}</p>
        <div v-for="(s, i) in socios" :key="s.chave" class="socio" :data-testid="`rede-ficha-socio-${i}`">
          <input-text v-model="s.nome" :elem-id="`rede-ficha-socio-nome-${i}`" :data-testid="`rede-ficha-socio-nome-${i}`" :label="t('Nome')" />
          <input-text v-model="s.cpf" :elem-id="`rede-ficha-socio-cpf-${i}`" :data-testid="`rede-ficha-socio-cpf-${i}`" :label="t('CPF')" />
          <input-date v-model="s.nascimento" :elem-id="`rede-ficha-socio-nascimento-${i}`" :data-testid="`rede-ficha-socio-nascimento-${i}`" :label="t('Nascimento')" />
          <input-text v-model="s.email" :elem-id="`rede-ficha-socio-email-${i}`" :data-testid="`rede-ficha-socio-email-${i}`" :label="t('E-mail')" />
          <span v-if="s.tem_login" class="login">{{ t('tem login') }}</span>
          <app-button icon="pi pi-trash" severity="secondary" text :aria-label="t('Tirar sócio')" :data-testid="`rede-ficha-socio-tirar-${i}`" @click="socios.splice(i, 1)" />
        </div>
        <app-button :label="t('Incluir sócio')" icon="pi pi-plus" size="small" severity="secondary" data-testid="rede-ficha-socio-incluir" @click="incluirSocio" />
      </section>

      <div class="botoes">
        <app-button type="submit" :label="t('Gravar ficha')" icon="pi pi-save" :loading="gravando" data-testid="rede-ficha-gravar" />
        <span v-if="ficha.atualizado_por" class="quieto">{{ t('Última gravação') }}: {{ ficha.atualizado_por }}</span>
      </div>
    </form>
  </Modal>
</template>

<script setup lang="ts">
import { onBeforeUnmount, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import InputDate from '../../inputs/InputDate.vue';
import InputText from '../../inputs/InputText.vue';
import InputTextArea from '../../inputs/InputTextArea.vue';
import { useRede, mensagemDaRede } from '../../composables/useRede';
import { eventBus } from '../../eventBus';
import type { Rede_FichaResponseDados } from '../../contracts/Rede_FichaResponse';

const { t } = useI18n();
const { carregarFicha, salvarFicha } = useRede();

type Campo = { campo: string; rotulo: string; tipo?: 'texto' | 'data' };
/** A ordem e os rótulos da tela; os nomes são os de FichaDaUnidade::CAMPOS no app-api. */
const SECOES: { titulo: string; campos: Campo[] }[] = [
  { titulo: 'Empresa', campos: [
    { campo: 'razao_social', rotulo: 'Razão social' }, { campo: 'nome_fantasia', rotulo: 'Nome fantasia' }, { campo: 'cnpj', rotulo: 'CNPJ' },
    { campo: 'inclusao_pj', rotulo: 'Inclusão da PJ no contrato' }, { campo: 'area_atuacao', rotulo: 'Área de atuação', tipo: 'texto' },
  ] },
  { titulo: 'Endereço e contato', campos: [
    { campo: 'endereco', rotulo: 'Endereço da sede' }, { campo: 'numero', rotulo: 'Número' }, { campo: 'complemento', rotulo: 'Complemento' },
    { campo: 'bairro', rotulo: 'Bairro' }, { campo: 'cidade', rotulo: 'Cidade' }, { campo: 'uf', rotulo: 'UF' }, { campo: 'pais', rotulo: 'País' },
    { campo: 'cep', rotulo: 'CEP' }, { campo: 'telefone', rotulo: 'Telefone' }, { campo: 'telefone_atendimento', rotulo: 'Telefone de atendimento' },
    { campo: 'email_empresa', rotulo: 'E-mail da empresa' },
  ] },
  { titulo: 'Mídias', campos: [{ campo: 'site', rotulo: 'Site' }, { campo: 'gmn', rotulo: 'Google Meu Negócio' }, { campo: 'redes_sociais', rotulo: 'Redes sociais', tipo: 'texto' }] },
  { titulo: 'Contrato', campos: [
    { campo: 'contrato_codigo', rotulo: 'Contrato vigente' }, { campo: 'contrato_data', rotulo: 'Data do contrato vigente', tipo: 'data' },
    { campo: 'vigencia', rotulo: 'Renovação / vigência (ex.: Renovado até 12/03/2029)' }, { campo: 'inclusao_factory', rotulo: 'Inclusão da Factory' },
    { campo: 'historico_contratos', rotulo: 'Histórico de contratos e aditivos', tipo: 'texto' },
  ] },
  { titulo: 'Acompanhamento', campos: [
    { campo: 'treinador', rotulo: 'Treinador' }, { campo: 'documentos_pendentes', rotulo: 'Documentos pendentes', tipo: 'texto' },
    { campo: 'pendencias_inauguracao', rotulo: 'Pendências de inauguração', tipo: 'texto' }, { campo: 'observacoes', rotulo: 'Observações', tipo: 'texto' },
    { campo: 'pontos_a_conferir', rotulo: 'Pontos a conferir', tipo: 'texto' },
  ] },
];

type Socio = { chave: number; nome: string; cpf: string; nascimento: string; email: string; tem_login: boolean };

const isVisible = ref(false);
const unidade = ref('');
const ficha = ref<Rede_FichaResponseDados | null>(null);
const valores = reactive<Record<string, string>>({});
const doCadastro = reactive<Record<string, string>>({});
const socios = ref<Socio[]>([]);
const gravando = ref(false);
let proximaChave = 0;

/** AAAA-MM-DD → DD/MM/AAAA, sem passar por Date (data sem hora não sofre fuso). */
const dataBr = (iso: string | null | undefined): string => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : '');

const preencher = (f: Rede_FichaResponseDados): void => {
  ficha.value = f;
  for (const c of f.campos) {
    valores[c.campo] = c.valor ?? '';
    doCadastro[c.campo] = c.do_cadastro ?? '';
  }
  socios.value = f.socios.map((s) => ({ chave: proximaChave++, nome: s.nome ?? '', cpf: s.cpf ?? '', nascimento: s.nascimento ?? '', email: s.email ?? '', tem_login: s.tem_login ?? false }));
};

const abrir = (dados?: { unidade: string }): void => {
  if (!dados?.unidade) return;
  unidade.value = dados.unidade;
  ficha.value = null;
  isVisible.value = true;
  carregarFicha(dados.unidade)
    .then(preencher)
    .catch((e: unknown) => eventBus.emit('globalMessage.error', t(mensagemDaRede(e))));
};

const fechar = (): void => {
  isVisible.value = false;
};

const incluirSocio = (): void => {
  socios.value.push({ chave: proximaChave++, nome: '', cpf: '', nascimento: '', email: '', tem_login: false });
};

const gravar = async (): Promise<void> => {
  gravando.value = true;
  try {
    const f = await salvarFicha({
      unidade: unidade.value,
      // Vazio vai como ausente: o app-api grava null.
      campos: Object.entries(valores).map(([campo, valor]) => ({ campo, valor: valor.trim() || undefined })),
      socios: socios.value.map((s) => ({ nome: s.nome.trim(), cpf: s.cpf.trim() || undefined, nascimento: s.nascimento || undefined, email: s.email.trim() || undefined })),
    });
    preencher(f);
    eventBus.emit('globalMessage.success', t('Ficha gravada'));
    eventBus.emit('rede.situacao.atualizada');
  } catch (e) {
    eventBus.emit('globalMessage.error', t(mensagemDaRede(e)));
  } finally {
    gravando.value = false;
  }
};

eventBus.on('modal.rede-ficha.open', abrir);
onBeforeUnmount(() => {
  eventBus.off('modal.rede-ficha.open', abrir);
});
</script>

<style scoped>
.ficha { display: flex; flex-direction: column; gap: 14px; }
.secao h4 { margin: 0 0 8px; font-size: 15px; }
.grade { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
.campo { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.campo.largo { grid-column: 1 / -1; }
.do-cadastro { color: #1a3e9e; font-size: 12px; }
.socio { display: grid; grid-template-columns: 2fr 1fr 1fr 1.5fr auto auto; gap: 8px; align-items: end; margin-bottom: 8px; }
.login { color: #1e7b4f; font-size: 12px; font-weight: 700; }
.botoes { display: flex; gap: 12px; align-items: center; }
.alerta { margin: 0; padding: 8px 12px; border-radius: 8px; background: #fff1d6; color: #9a6700; font-weight: 700; }
.quieto { color: #5b6168; font-size: 13px; margin: 0; }
@media (max-width: 800px) { .grade { grid-template-columns: minmax(0, 1fr); } .socio { grid-template-columns: minmax(0, 1fr); } }
</style>
